import { DeleteOutlined, EditOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Form, Input, InputNumber, message, Modal, Space, Table, TableColumnsType, Tag, Tour, ConfigProvider } from 'antd';
import type { TourProps } from 'antd';
import React, { useEffect, useState, useRef } from 'react';
import { flavorService } from '../services/flavorService';
import type { Flavor } from '../types/flavor';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import '../styles/AdminPages.css';
import viVN from 'antd/locale/vi_VN';

const { TextArea, Search } = Input;

const FlavorForm: React.FC<{
  onIconChange: (file: File | null) => void,
  selectedFileName: string | null,
  isMobile?: boolean
}> = ({ 
  onIconChange, 
  selectedFileName,
  isMobile = false 
}) => {
    return (
        <div className={`admin-form-container ${isMobile ? 'mobile-coffee-form' : ''}`}>
            <div className="admin-form-section">
                <h3 className="admin-form-section-title">Thông tin cơ bản</h3>
                <Form.Item 
                    name="name" 
                    label="Tên hương vị" 
                    rules={[{ required: true, message: 'Vui lòng nhập tên hương vị' }]}
                >
                    <Input placeholder="Nhập tên hương vị (VD: Hương cà phê)" />
                </Form.Item>

                <Form.Item 
                    name="description" 
                    label="Mô tả"
                >
                    <TextArea 
                        rows={4} 
                        placeholder="Nhập mô tả chi tiết về hương vị (VD: Hương vị đậm đà, thơm ngon...)" 
                    />
                </Form.Item>

                <Form.Item 
                    name="intensity" 
                    label="Cường độ"
                >
                    <InputNumber 
                        min={1} 
                        max={10} 
                        placeholder="Chọn từ 1-10"
                        style={{ width: '100%' }}
                    />
                </Form.Item>
            </div>

            <div className="admin-form-section">
                <h3 className="admin-form-section-title">Icon</h3>
                <Form.Item 
                    label="Icon"
                    rules={[{ required: true, message: 'Vui lòng chọn icon cho hương vị' }]}
                >
                    <div className="admin-image-upload-section">
                        <Space direction="vertical">
                            <Button onClick={() => document.getElementById('fileInput')?.click()}>
                                Chọn ảnh
                            </Button>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => onIconChange(e.target.files?.[0] || null)}
                                id="fileInput"
                                style={{ display: 'none' }}
                            />
                            {selectedFileName && (
                                <Tag color="blue">
                                    File đã chọn: {selectedFileName}
                                </Tag>
                            )}
                        </Space>
                    </div>
                </Form.Item>
            </div>
        </div>
    );
};

const FlavorManagement: React.FC = () => {
    const [flavors, setFlavors] = useState<Flavor[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingFlavor, setEditingFlavor] = useState<Flavor | null>(null);
    const [form] = Form.useForm();
    const [iconFile, setIconFile] = useState<File | null>(null);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const isMobile = windowWidth <= 400;
    const [submitLoading, setSubmitLoading] = useState(false);
    const [searchText, setSearchText] = useState('');

    // Tour state and refs
    const [isTourOpen, setIsTourOpen] = useState(false);
    const titleRef = useRef(null);
    const searchRef = useRef(null);
    const addButtonRef = useRef(null);
    const tableContainerRef = useRef(null);

    const tourSteps: TourProps['steps'] = [
        {
            title: 'Quản lý hương vị',
            description: 'Chào mừng bạn đến với trang Quản lý hương vị! Đây là nơi bạn có thể quản lý danh sách các hương vị cà phê, thêm mới, chỉnh sửa và cập nhật thông tin chi tiết về từng hương vị.',
            target: () => titleRef.current,
        },
        {
            title: 'Tìm kiếm',
            description: 'Tìm kiếm nhanh các hương vị theo tên hoặc mô tả. Nhập từ khóa và kết quả sẽ được lọc ngay lập tức.',
            target: () => searchRef.current,
        },
        {
            title: 'Thêm hương vị mới',
            description: 'Nhấn vào đây để thêm một hương vị mới vào hệ thống. Bạn sẽ cần điền thông tin như tên hương vị, mô tả, cường độ và chọn icon minh họa.',
            target: () => addButtonRef.current,
        },
        {
            title: 'Bảng dữ liệu hương vị',
            description: 'Bảng hiển thị tất cả các hương vị đã được thêm vào hệ thống. Mỗi hàng chứa thông tin về hương vị bao gồm icon, tên, mô tả, cường độ và các nút thao tác để chỉnh sửa hoặc xóa.',
            target: () => tableContainerRef.current,
        },
        {
            title: 'Kết thúc hướng dẫn!',
            description: 'Cảm ơn bạn đã xem hướng dẫn này. Bây giờ bạn đã sẵn sàng quản lý danh mục hương vị cà phê. Bạn có thể thêm mới, chỉnh sửa, xóa các hương vị và sử dụng tìm kiếm để tìm kiếm nhanh chóng.',
            target: () => titleRef.current,
        },
    ];

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        fetchFlavors();
    }, []);

    const fetchFlavors = async () => {
        setLoading(true);
        try {
            const data = await flavorService.getAllFlavors();
            setFlavors(data);
        } catch (error) {
            console.error('Error fetching flavors:', error);
            message.error('Không thể tải danh sách hương vị');
        }
        setLoading(false);
    };

    const handleSubmit = async (values: any) => {
        try {
            setSubmitLoading(true);
            const flavorData: Flavor = {
                name: values.name,
                description: values.description || '',
                intensity: values.intensity || null,
            };

            if (editingFlavor) {
                await flavorService.updateFlavor(editingFlavor.id!, flavorData, iconFile || undefined);
            } else {
                await flavorService.addFlavor(flavorData, iconFile || undefined);
            }
            setModalVisible(false);
            form.resetFields();
            setIconFile(null);
            fetchFlavors();
            message.success(`${editingFlavor ? 'Cập nhật' : 'Thêm'} hương vị thành công`);

            // Gọi api gửi dữ liệu hương vị để AI tự động cập nhật
            // await axios.get(`https://coffee-ai.8am.vn/update`);
        } catch (error) {
            console.error('Error saving flavor:', error);
            message.error('Có lỗi xảy ra khi lưu thông tin');
        } finally {
            setSubmitLoading(false);
        }
    };

    const columns: TableColumnsType<Flavor> = [
        {
            title: 'Icon',
            dataIndex: 'iconUrl',
            key: 'iconUrl',
            render: (url: string) => url ? <img src={url} alt="flavor icon" style={{ width: 40, height: 40, objectFit: 'contain' }} /> : null
        },
        {
            title: 'Tên hương vị',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Mô tả',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: 'Cường độ',
            dataIndex: 'intensity',
            key: 'intensity',
        },
        {
            title: 'Thao tác',
            key: 'action',
            fixed: 'right',
            width: 100,
            render: (_: any, record: Flavor) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => {
                            setEditingFlavor(record);
                            form.setFieldsValue(record);
                            setModalVisible(true);
                        }}
                    />
                    <Button
                        icon={<DeleteOutlined />}
                        danger
                        onClick={() => handleDelete(record.id!)}
                    />
                </Space>
            )
        }
    ];

    const getMobileColumns = (): TableColumnsType<Flavor> => [
        {
            title: 'Thông tin',
            dataIndex: 'name',
            key: 'info',
            render: (_, record) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                        {record.iconUrl && (
                            <img 
                                src={record.iconUrl} 
                                alt="flavor icon" 
                                style={{ width: 40, height: 40, objectFit: 'contain' }}
                            />
                        )}
                        <div style={{ flex: 1 }}>
                            <strong>{record.name}</strong>
                            <div style={{
                                marginTop: 4,
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                fontSize: '12px',
                                color: '#666'
                            }}>
                                {record.description}
                            </div>
                        </div>
                    </div>
                    <div style={{ fontSize: '12px' }}>
                        <Tag color="blue">Cường độ: {record.intensity}</Tag>
                    </div>
                </div>
            )
        },
        {
            title: 'Thao tác',
            key: 'action',
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => {
                            setEditingFlavor(record);
                            form.setFieldsValue(record);
                            setModalVisible(true);
                        }}
                    />
                    <Button
                        icon={<DeleteOutlined />}
                        danger
                        onClick={() => handleDelete(record.id!)}
                    />
                </Space>
            )
        }
    ];

    const handleDelete = async (id: string) => {
        Modal.confirm({
            title: 'Xác nhận xóa',
            content: 'Bạn có chắc chắn muốn xóa hương vị này?',
            okText: 'Xóa',
            okType: 'danger',
            cancelText: 'Hủy',
            async onOk() {
                try {
                    await flavorService.deleteFlavor(id);
                    fetchFlavors();
                    message.success('Xóa hương vị thành công');

                    // Gọi api gửi dữ liệu hương vị để AI tự động cập nhật
                    // await axios.get(`https://coffee-ai.8am.vn/update`);
                } catch (error) {
                    console.error('Error deleting flavor:', error);
                    message.error('Không thể xóa hương vị');
                }
            }
        });
    };

    const getFilteredFlavors = () => {
        return flavors.filter(flavor => {
            const matchesSearch = flavor.name.toLowerCase().includes(searchText.toLowerCase()) ||
                                 (flavor.description?.toLowerCase() || '').includes(searchText.toLowerCase());
            return matchesSearch;
        });
    };

    return (
        <AdminLayout>
            <div className="admin-page-header">
                <div className="admin-page-title">
                    <h1 ref={titleRef}>Quản lý hương vị</h1>
                    <Tag color="blue">{flavors.length} hương vị</Tag>
                    <Button 
                        type="primary" 
                        icon={<QuestionCircleOutlined />} 
                        onClick={() => setIsTourOpen(true)}
                        style={{ marginLeft: 8 }}
                    >
                        Hướng dẫn
                    </Button>
                </div>
                
                <div className="admin-page-actions">
                    <div className="admin-search-filters">
                        <div ref={searchRef}>
                            <Search
                                placeholder="Tìm theo tên hoặc mô tả"
                                allowClear
                                style={{ width: isMobile ? '100%' : 300 }}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                        </div>
                    </div>

                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setEditingFlavor(null);
                            form.resetFields();
                            setIconFile(null);
                            setModalVisible(true);
                        }}
                        className="admin-button"
                        ref={addButtonRef}
                    >
                        Thêm hương vị mới
                    </Button>
                </div>
            </div>

            <div className="admin-table-container" ref={tableContainerRef}>
                <Table
                    className="admin-table"
                    columns={isMobile ? getMobileColumns() : columns}
                    dataSource={getFilteredFlavors()}
                    loading={loading}
                    rowKey="id"
                    scroll={{ x: isMobile ? undefined : 1500 }}
                    pagination={{
                        responsive: true,
                        defaultPageSize: isMobile ? 5 : 10,
                        position: isMobile ? ['bottomCenter'] : ['bottomRight']
                    }}
                />
            </div>

            {/* Tour component */}
            <ConfigProvider locale={viVN}>
                <Tour
                    open={isTourOpen}
                    onClose={() => setIsTourOpen(false)}
                    steps={tourSteps}
                    indicatorsRender={(current, total) => (
                        <span>{current + 1} / {total}</span>
                    )}
                    type="primary"
                    arrow={true}
                    placement="bottom"
                    mask={{
                        style: {
                            boxShadow: 'inset 0 0 15px #333',
                        },
                    }}
                />
            </ConfigProvider>

            <Modal
                title={editingFlavor ? "Sửa thông tin hương vị" : "Thêm hương vị mới"}
                visible={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    setIconFile(null);
                    form.resetFields();
                }}
                footer={null}
                width={800}
                className="admin-modal"
            >
                <div className="admin-form-container">
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                    >
                        <FlavorForm 
                            onIconChange={setIconFile} 
                            selectedFileName={iconFile?.name || null}
                            isMobile={isMobile}
                        />
                        <Form.Item>
                            <Button 
                                type="primary" 
                                htmlType="submit"
                                loading={submitLoading}
                                disabled={submitLoading}
                                className="admin-button"
                            >
                                {editingFlavor ? 'Cập nhật' : 'Thêm mới'}
                            </Button>
                        </Form.Item>
                    </Form>
                </div>
            </Modal>
        </AdminLayout>
    );
};

export default FlavorManagement;